import { CameraOff, Flashlight, FlashlightOff, ImageUp, RotateCcw, X } from "lucide-react";
import { useEffect, useRef } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { useQrScanner } from "../hooks/useQrScanner";
import type { CameraFailure } from "../utils/cameraErrors";

export interface QrScannerOverlayProps {
  onDetected: (raw: string) => void;
  onClose: () => void;
  onUploadImage: () => void;
}

const FAILURE_KEYS = {
  insecure: "camera_insecure",
  unsupported: "camera_unsupported",
  denied: "camera_denied_text",
  not_found: "camera_not_found",
  in_use: "camera_in_use",
  error: "camera_error",
} as const satisfies Record<CameraFailure, string>;

/** Not portalled: outside the send drawer's popup, a tap on the camera would close the drawer. */
export function QrScannerOverlay({ onDetected, onClose, onUploadImage }: QrScannerOverlayProps) {
  const { t } = useTranslations("brebQrPay");
  const videoRef = useRef<HTMLVideoElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const scanner = useQrScanner(videoRef, onDetected);

  useEffect(() => {
    closeRef.current?.focus();
    // Captured and stopped so Escape closes the camera only, not the drawer it sits in.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("camera_title")}
      className="fixed inset-0 z-[60] flex flex-col bg-zinc-950 text-white"
    >
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        aria-hidden="true"
        className={cn(
          "absolute inset-0 size-full object-cover transition-opacity duration-300",
          scanner.status === "active" ? "opacity-100" : "opacity-0",
        )}
      />

      <div className="relative z-10 flex items-center justify-between gap-3 px-4 pt-[max(1rem,env(safe-area-inset-top))]">
        <p className="text-base font-semibold drop-shadow">{t("camera_title")}</p>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label={t("camera_close")}
          className="flex size-11 items-center justify-center rounded-full bg-black/45 backdrop-blur transition hover:bg-black/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <X className="size-5" aria-hidden="true" />
        </button>
      </div>

      <div className="relative z-10 flex flex-1 items-center justify-center px-8">
        {scanner.status === "failed" ? (
          <div role="alert" className="flex max-w-xs flex-col items-center gap-4 text-center">
            <span className="flex size-14 items-center justify-center rounded-2xl bg-white/10">
              <CameraOff className="size-7" aria-hidden="true" />
            </span>
            {scanner.failure === "denied" && <p className="font-semibold">{t("camera_denied_title")}</p>}
            <p className="text-sm text-white/75">{t(FAILURE_KEYS[scanner.failure ?? "error"])}</p>
            {scanner.failure !== "insecure" && scanner.failure !== "unsupported" && (
              <ColocaButton type="button" variant="secondary" icon={RotateCcw} onClick={scanner.retry}>
                {t("camera_retry")}
              </ColocaButton>
            )}
          </div>
        ) : (
          <div className="relative aspect-square w-full max-w-[280px]">
            {/* Corner brackets only: a full frame would hide the edge of a code that is slightly off-centre. */}
            {[
              "top-0 left-0 border-t-4 border-l-4 rounded-tl-3xl",
              "top-0 right-0 border-t-4 border-r-4 rounded-tr-3xl",
              "bottom-0 left-0 border-b-4 border-l-4 rounded-bl-3xl",
              "bottom-0 right-0 border-b-4 border-r-4 rounded-br-3xl",
            ].map((corner) => (
              <span
                key={corner}
                aria-hidden="true"
                className={cn("absolute size-12 border-emerald-400", corner)}
              />
            ))}
            {scanner.status === "active" && (
              <span
                aria-hidden="true"
                className="absolute inset-x-6 top-1/2 h-0.5 rounded-full bg-emerald-400/80 shadow-[0_0_16px_2px_rgba(52,211,153,0.6)] motion-safe:animate-pulse"
              />
            )}
            {scanner.status === "starting" && (
              <p
                aria-live="polite"
                className="absolute inset-0 flex items-center justify-center text-sm text-white/80"
              >
                {t("camera_starting")}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="relative z-10 flex flex-col items-center gap-4 px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        {scanner.status !== "failed" && (
          <p
            aria-live="polite"
            className={cn(
              "rounded-full px-4 py-2 text-sm backdrop-blur transition-colors",
              scanner.sawForeignQr ? "bg-amber-500/90 text-zinc-950" : "bg-black/45 text-white/90",
            )}
          >
            {scanner.sawForeignQr ? t("camera_foreign_qr") : t("camera_hint")}
          </p>
        )}
        <div className="flex items-center gap-3">
          {scanner.torchSupported && (
            <ColocaButton
              type="button"
              variant="secondary"
              icon={scanner.torchOn ? FlashlightOff : Flashlight}
              onClick={scanner.toggleTorch}
              aria-pressed={scanner.torchOn}
            >
              {scanner.torchOn ? t("camera_torch_off") : t("camera_torch_on")}
            </ColocaButton>
          )}
          <ColocaButton type="button" variant="secondary" icon={ImageUp} onClick={onUploadImage}>
            {t("camera_upload")}
          </ColocaButton>
        </div>
      </div>
    </div>
  );
}
